import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star, Save, X } from "lucide-react";
import { adminApi, publicApi } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", location: "", rating: 5, quote: "", journey: "", avatar: "" };

export default function AdminTestimonials() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | {id...}
  const [form, setForm] = useState(EMPTY);

  const load = () => publicApi.get("/testimonials").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (t) => { setEditing(t); setForm({ ...t }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        const r = await adminApi.post("/testimonials", form);
        setList((l) => [...l, r.data]);
        toast.success("Testimonial added");
      } else {
        const r = await adminApi.put(`/testimonials/${editing.id}`, form);
        setList((l) => l.map((x) => (x.id === editing.id ? r.data : x)));
        toast.success("Saved");
      }
      cancel();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from ${t.name}?`)) return;
    try {
      await adminApi.delete(`/testimonials/${t.id}`);
      setList((l) => l.filter((x) => x.id !== t.id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const inputCls = "w-full bg-transparent border border-border focus:border-accent transition-colors px-4 py-3 outline-none text-sm";

  return (
    <div className="space-y-8" data-testid="admin-testimonials-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-3">Guest Voices</div>
          <h1 className="font-serif text-4xl md:text-5xl">Testimonials.</h1>
        </div>
        <button onClick={startNew} data-testid="testimonial-new" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent transition-colors uppercase text-[11px] tracking-[0.25em]">
          <Plus size={14} strokeWidth={1.5} /> New testimonial
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="border border-accent bg-secondary/30 p-6 md:p-8 space-y-5" data-testid="testimonial-form">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl">{editing === "new" ? "New testimonial" : `Edit · ${editing.name}`}</h3>
            <button type="button" onClick={cancel} className="p-2 hover:text-accent"><X size={16} strokeWidth={1.5} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input required value={form.name} onChange={set("name")} placeholder="Guest name" className={inputCls} />
            <input required value={form.location} onChange={set("location")} placeholder="Location (e.g. London, UK)" className={inputCls} />
            <input required value={form.journey} onChange={set("journey")} placeholder="Journey (e.g. Rajasthan · 12 Nights)" className={inputCls} />
            <input type="number" min={1} max={5} value={form.rating} onChange={set("rating")} placeholder="Rating 1-5" className={inputCls} />
            <input value={form.avatar} onChange={set("avatar")} placeholder="Avatar URL" className={`${inputCls} md:col-span-2`} />
            <textarea required value={form.quote} onChange={set("quote")} placeholder="The quote (1-3 sentences)" rows={4} className={`${inputCls} resize-none md:col-span-2`} />
          </div>
          <button type="submit" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent transition-colors uppercase text-[11px] tracking-[0.25em]">
            <Save size={14} strokeWidth={1.5} /> Save
          </button>
        </form>
      )}

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((t) => (
          <li key={t.id} className="border border-border bg-background p-6 flex flex-col">
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={12} fill="currentColor" strokeWidth={0} className="text-accent" />)}
            </div>
            <p className="font-serif text-lg leading-snug flex-1">"{t.quote}"</p>
            <div className="mt-5 pt-4 border-t border-border flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-3">
                {t.avatar && <img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full object-cover shrink-0" />}
                <div className="min-w-0">
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 truncate">{t.journey}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => startEdit(t)} className="p-2 border border-border hover:border-foreground transition-colors">
                  <Pencil size={12} strokeWidth={1.5} />
                </button>
                <button onClick={() => remove(t)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 size={12} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
